import { CrcSelect, ICrcSelectOption, PageHeader, routing, t, useMapDispatch, useMapState, useMapStateMemoize } from "@comact/crc";
import { Buttons, ButtonsContainer, Line, ListGrid, LoadingSpinner, MessageNoResults, Page, PageBox, Span, styled, Table } from "@comact/crc/modules/kit";
import _ from "lodash";
import React from "react";
import { SalesforceModule } from "..";
import { PageNode, usePageNodeContext } from "../../node/components/PageNode";
import { Levels, Permissions, UserModule } from "../../users";
import { ISalesforceURLSearchParams } from "../model";
import SalesforceCasesRow from "./SalesforceCasesRow";

const ButtonsContainerStyled = styled(ButtonsContainer)`
    margin-top: var(--gutter);
    align-items: center;
`;

const DEFAULT_STATUS_GROUP = "Open_Statuses";

const useGetUrlSearchParams = () => {
    const urlSearchParams = Object.fromEntries(new URLSearchParams(window.location.search)) as unknown as ISalesforceURLSearchParams;
    const data = React.useRef(urlSearchParams);
    if (!_.isEqual(data.current, urlSearchParams)) {
        data.current = urlSearchParams;
    }
    return data.current;
};

const PageSalesforceCases = React.memo(() => {
    const { nodeId } = usePageNodeContext();
    const urlSearchParams = useGetUrlSearchParams();
    const pageNumber = urlSearchParams.pageNumber ? Number(urlSearchParams.pageNumber) : 1;
    const { getCaseStatuses, getCaseContacts, getCaseMachines, getCases } = useMapDispatch(SalesforceModule.getRequests);
    const hasCreateAccess = useMapState((state) => UserModule.selectors.hasPermission(state, [{ permission: Permissions.ServiceRequest, level: Levels.Edit }], nodeId));

    const { statusesOptions, contactsOptions, machinesOptions, contactFilterMetadataLoaded, defaultContactId } = useMapStateMemoize(({ salesforceMetadata }) => ({
        statusesOptions: [
            { value: DEFAULT_STATUS_GROUP, label: t("salesforce.filters.openRequests") },
            { value: "", label: t("salesforce.filters.anyStatus") },
            ..._.map(salesforceMetadata.statuses ?? {}, (label, value) => ({ value, label })),
        ],
        contactsOptions: [
            { value: "", label: t("salesforce.filters.anyContact") },
            ..._.map(salesforceMetadata.contacts ?? [], ({ id, name }) => ({ value: id, label: name })),
        ],
        machinesOptions: [
            { value: "", label: t("salesforce.filters.anyMachine") },
            ..._.map(salesforceMetadata.machines ?? [], (value) => ({ value, label: value })),
        ],
        contactFilterMetadataLoaded: salesforceMetadata.contacts !== null,
        defaultContactId: salesforceMetadata.defaultContactId,
    }));

    const [hasUrlLoaded, setHasUrlLoaded] = React.useState<boolean>(false);

    const createSearchUrl = React.useCallback((partialurlSearchParams: Partial<ISalesforceURLSearchParams>) => {
        const newUrlSearchParams = new URLSearchParams(_.pickBy({ ...urlSearchParams, ...partialurlSearchParams }, (v) => !_.isEmpty(v)));
        return routing.createUrl(`/customer/requests/${nodeId}?${newUrlSearchParams.toString()}`);
    }, [urlSearchParams, nodeId]);

    const onCreateTicket = React.useCallback(() => {
        routing.goToUrl(`/customer/requests/create/${nodeId}`);
    }, [nodeId]);

    React.useEffect(() => {
        if (!contactFilterMetadataLoaded || hasUrlLoaded) return;

        const currentSearchParams = new URLSearchParams(window.location.search);
        const defaultSearchParams: Partial<ISalesforceURLSearchParams> = {};
        if (!currentSearchParams.has("status")) defaultSearchParams.status = DEFAULT_STATUS_GROUP;
        if (!currentSearchParams.has("contactId") && defaultContactId) defaultSearchParams.contactId = defaultContactId;

        if (!_.isEmpty(defaultSearchParams)) {
            defaultSearchParams.pageNumber = "1";
            routing.goToUrl(createSearchUrl(defaultSearchParams));
        }
        setHasUrlLoaded(true);
    }, [contactFilterMetadataLoaded, createSearchUrl, defaultContactId, hasUrlLoaded]);

    React.useEffect(() => {
        getCaseStatuses(nodeId);
        getCaseContacts(nodeId);
        getCaseMachines(nodeId);
    }, [getCaseContacts, getCaseMachines, getCaseStatuses, nodeId]);

    React.useEffect(() => {
        if (!hasUrlLoaded) return;
        getCases(nodeId, urlSearchParams);
    }, [nodeId, getCases, urlSearchParams, hasUrlLoaded]);

    const onChangeStatus = React.useCallback((o: ICrcSelectOption) => routing.goToUrl(createSearchUrl({
        status: o?.value === DEFAULT_STATUS_GROUP ? DEFAULT_STATUS_GROUP : o?.value, pageNumber: "1",
    })), [createSearchUrl]);
    const onChangeContact = React.useCallback((o: ICrcSelectOption) => routing.goToUrl(createSearchUrl({ contactId: o?.value, pageNumber: "1" })), [createSearchUrl]);
    const onChangeMachine = React.useCallback((o: ICrcSelectOption) => routing.goToUrl(createSearchUrl({ machine: o?.value, pageNumber: "1" })), [createSearchUrl]);

    const { items, totalRecords, currentPage } = useMapState((state) => state.salesforceResults);

    if (!nodeId || !items) return <LoadingSpinner.Absolute />;

    return (
        <>
            <PageHeader>
                <ListGrid kMinSize={180} kWidth="600px">
                    <CrcSelect
                        placeholder={t("salesforce.filters.anyStatus")}
                        options={statusesOptions}
                        value={_.find(statusesOptions, (o) => o.value == urlSearchParams.status)}
                        onChange={onChangeStatus}
                        isClearable
                    />
                    <CrcSelect
                        placeholder={t("salesforce.filters.anyContact")}
                        options={contactsOptions}
                        value={_.find(contactsOptions, (o) => o.value == urlSearchParams.contactId)}
                        onChange={onChangeContact}
                        isClearable
                    />
                    <CrcSelect
                        placeholder={t("salesforce.filters.anyMachine")}
                        options={machinesOptions}
                        value={_.find(machinesOptions, (o) => o.value == urlSearchParams.machine)}
                        onChange={onChangeMachine}
                        isClearable
                    />
                </ListGrid>
                <PageHeader.Spacer />
                {hasCreateAccess && <Buttons.Add onClick={onCreateTicket}>{t("salesforce.buttons.create")}</Buttons.Add>}
            </PageHeader>
            <Page kContainerSize="100%">
                <PageBox>
                    {totalRecords == 0 && (
                        <MessageNoResults />
                    )}
                    {totalRecords != 0 && (
                        <>
                            <Table.ResponsiveContainer>
                                <Table.table kMode="card">
                                    <Table.thead>
                                        <Table.tr>
                                            <Table.th kWidth="5%">{t("salesforce.caseColumns.caseNumber")}</Table.th>
                                            <Table.th kWidth="40%" kMaxWidth="400px">{t("salesforce.caseColumns.subject")}</Table.th>
                                            <Table.th kWidth="15%">{t("salesforce.caseColumns.machine")}</Table.th>
                                            <Table.th kWidth="10%">{t("salesforce.caseColumns.responsibleTeam")}</Table.th>
                                            <Table.th kWidth="10%">{t("salesforce.caseColumns.caseOwner")}</Table.th>
                                            <Table.th kWidth="10%">{t("salesforce.caseColumns.author")}</Table.th>
                                            <Table.th kWidth="5%">{t("salesforce.caseColumns.created")}</Table.th>
                                            <Table.th kWidth="5%">{t("salesforce.caseColumns.status")}</Table.th>
                                        </Table.tr>
                                    </Table.thead>
                                    <Table.tbody>
                                        {_.map(items[currentPage], (salesforceCase) => {
                                            const opt = statusesOptions.find((o) => o.label === salesforceCase.status);
                                            return (
                                                <SalesforceCasesRow
                                                    {...salesforceCase}
                                                    key={salesforceCase.id}
                                                    statusKey={opt?.value ?? salesforceCase.status}
                                                    statusLabel={salesforceCase.status}
                                                />
                                            );
                                        })}
                                    </Table.tbody>
                                </Table.table>
                            </Table.ResponsiveContainer>
                            <Pagination pageNumber={pageNumber} createSearchUrl={createSearchUrl} />
                        </>
                    )}
                </PageBox>
            </Page>
        </>
    );
});

const Pagination = React.memo(({ pageNumber, createSearchUrl }: { pageNumber: number; createSearchUrl: (partialurlSearchParams: Partial<ISalesforceURLSearchParams>) => string; }) => {
    const { totalPages, pageSize, totalRecords } = useMapState((state) => state.salesforceResults);
    return (
        <ButtonsContainerStyled>
            <Line>
                <Span kFontWeight="black">{(pageNumber - 1) * pageSize + 1}</Span>
                <Span kFontWeight="light" kColor="light">{" - "}</Span>
                <Span kFontWeight="light">{_.clamp(pageNumber * pageSize, totalRecords)}</Span>
                <Span kFontWeight="light" kColor="light"> {t("salesforce.of")} </Span>
                <Span kFontWeight="bold" kColor="light">{totalRecords}</Span>
            </Line>
            {totalPages > 1 && (
                <>
                    <Buttons.Primary href={createSearchUrl({ pageNumber: `${pageNumber - 1}` })} disabled={pageNumber <= 1} title={t("salesforce.buttons.previous")}>
                        <i className="fa-solid fa-chevron-left" />
                    </Buttons.Primary>
                    <Buttons.Primary href={createSearchUrl({ pageNumber: `${pageNumber + 1}` })} disabled={pageNumber >= totalPages} title={t("salesforce.buttons.next")}>
                        <i className="fa-solid fa-chevron-right" />
                    </Buttons.Primary>
                </>
            )}
        </ButtonsContainerStyled>
    );
});

export default () => (
    <PageNode perspective="customer/requests">
        <PageSalesforceCases />
    </PageNode>
);
